import { Component, Show, createEffect, createMemo, createSignal, on } from 'solid-js'
import Button from '/web/shared/Button'
import { HelpModal, RootModal } from '/web/shared/Modal'
import { JsonSchema } from '/web/shared/JsonSchema'
import TextInput from '/web/shared/TextInput'
import { FormLabel } from '/web/shared/FormLabel'
import { ResponseSchema } from '/common/types/library'
import { Card, Pill, SolidCard, TitleCard } from '/web/shared/Card'
import { JSON_NAME_RE, neat } from '/common/util'
import { JsonField } from '/common/prompt'
import { AutoComplete } from '/web/shared/AutoComplete'
import { characterStore, chatStore, i18nStore, presetStore, responseStore, toastStore } from '/web/store'
import { CircleHelp } from 'lucide-solid'
import { downloadJson, ExtractProps } from '/web/shared/util'
import FileInput, { getFileAsString } from '/web/shared/FileInput'
import { assertValid } from '/common/valid'
import { useAppContext } from '/web/store/context'
import { createStore } from 'solid-js/store'
import { usePresetContext } from '/web/store/preset-context'
import { AppSchema } from '/common/types'
import { Option } from '/web/shared/Select'

const helpMarkdown = neat`

**IMPORTANT**: \`{{response}}\` is a _default_ schema value. It is **ALWAYS** generated!

You can return many values using JSON schemas and control the structure of your response.
For example you could define the following fields:
- **response**: string
- **character health percentage**: number

You can then reference these fields in your **response** and **history** templates:

Response Template
\`\`\`
{{response}}
\`HP: {{character health percentage}}%\`
\`\`\`

History Template
\`\`\`
{{response}}
(User's health: {{character health percentage}}%)
\`\`\`

**Tips**:
- Use instructive fields names. This tells the AI how to generate that field.
- If a field is disabled, it won't be included in the generation.
- Order may be important. For example, if you have a multi-character schema you may name the fields like this:
-- **Steve's message to Linda**
-- **Linda's response to Steve**
`

const exampleSchema: ResponseSchema = {
  history: '{{response}}',
  response: '{{response}}',
  imageCaption: ``,
  schema: [],
  systemPrompt: '',
  jailbreak: '',
}

export const CharacterSchema: Component<{
  characterId?: string
  preset?: AppSchema.GenSettings & { _id: string }
  children?: any
  update: (next: ResponseSchema) => void
}> = (props) => {
  const i18n = i18nStore((s) => ({ t: s.t }))
  const [ctx] = useAppContext()
  const [preset] = usePresetContext()

  let respRef: HTMLTextAreaElement
  let histRef: HTMLTextAreaElement
  let captionRef: HTMLTextAreaElement

  const [show, setShow] = createSignal(false)
  const [showImport, setShowImport] = createSignal(false)
  const [auto, setAuto] = createSignal('')
  const [hotkey, setHotkey] = createSignal(false)
  const [result, setResult] = createSignal<any>()
  const [store, setStore] = createStore({
    response: '',
    history: '',
    imageCaption: '',
    schema: [] as JsonField[],
    systemPrompt: '',
    jailbreak: '',
  })

  const vars = createMemo(() => {
    const list = store.schema.reduce((prev, curr) => {
      prev.push({ label: curr.name, value: curr.name })

      if (curr.alias) {
        prev.push({ label: curr.alias, value: curr.alias })
      }

      return prev
    }, [] as Option[])
    return list
  })

  const [resErr, setResErr] = createSignal('')
  const [histErr, setHistErr] = createSignal('')
  const [captionErr, setCaptionErr] = createSignal('')

  const runSchemaTest = () => {
    setResult()
    responseStore.chatQuery(
      {
        fields: store.schema,
        assistant: 'Chat Analysis',
        question: 'Details from the above conversation:',
      },
      (resp, state, json) => {
        switch (state) {
          case 'headers': {
            if (`${resp}` === `200`) setResult(i18n.t.char_jsonSchemaGenerationStatus)
            break
          }

          case 'partial':
          case 'done':
            setResult(json || resp)
            return
        }
      }
    )
  }

  const saveTarget = createMemo(() => {
    if (!props.preset) return

    switch (props.preset.jsonSource) {
      case undefined:
      case 'preset':
      case 'character': {
        return { label: 'Current Preset', id: props.preset._id }
      }

      case 'json-preset': {
        return { label: 'JSON Preset', id: preset.json?._id }
      }
    }
  })

  createEffect(
    on(
      () => show(),
      (open) => {
        if (!open) return
        let json: ResponseSchema | undefined

        if (props.characterId) {
          const char = ctx.allBots[props.characterId]
          json = char ? char.json : ctx.char?.json
        } else if (props.preset) {
          switch (props.preset.jsonSource) {
            case 'character': {
              json = ctx.char?.json
              break
            }

            case undefined:
            case 'preset': {
              json = props.preset.json
              break
            }

            case 'json-preset': {
              json = preset.json?.json
              break
            }
          }
        }

        const hasValue =
          !!json?.schema?.length || !!json?.history || !!json?.response || !!json?.imageCaption
        if (json && hasValue) {
          setStore({
            schema: json.schema || [],
            history: json.history || '',
            response: json.response || '',
            imageCaption: json.imageCaption || '',
          })
        } else {
          setStore({
            history: exampleSchema.history,
            response: exampleSchema.response,
            imageCaption: exampleSchema.imageCaption,
            schema: exampleSchema.schema.slice(),
          })
        }
      }
    )
  )

  createEffect(() => {
    const resVars = store.response.match(JSON_NAME_RE())
    const histVars = store.history.match(JSON_NAME_RE())
    const captionVars = store.imageCaption.match(JSON_NAME_RE())

    const names = new Set(store.schema.map((s) => s.name))

    for (const field of store.schema) {
      if (field.alias) names.add(field.alias)
    }

    if (resVars) {
      const bad: string[] = []
      for (const res of resVars) {
        const name = res.slice(2, -2)
        if (!names.has(name) && name !== 'response') {
          bad.push(name)
        }
      }
      setResErr(bad.length ? bad.join(', ') : '')
    }

    if (histVars) {
      const bad: string[] = []
      for (const res of histVars) {
        const name = res.slice(2, -2)
        if (!names.has(name) && name !== 'response') {
          bad.push(name)
        }
      }
      setHistErr(bad.length ? bad.join(', ') : '')
    }

    if (captionVars) {
      const bad: string[] = []
      for (const res of captionVars) {
        const name = res.slice(2, -2)
        if (!names.has(name) && name !== 'response') {
          bad.push(name)
        }
      }

      setCaptionErr(bad.length ? bad.join(', ') : '')
    }
  })

  const onFieldNameChange = (from: string, to: string) => {
    const res = store.response.split(`{{${from}}}`).join(`{{${to}}}`)
    const his = store.history.split(`{{${from}}}`).join(`{{${to}}}`)

    setStore({ history: his, response: res })
  }

  const onAutoComplete =
    (field: 'history' | 'response' | 'imageCaption') => (opt: { label: string }) => {
      let ref = undefined

      switch (auto()) {
        case 'history':
          ref = histRef
          break

        case 'response':
          ref = respRef
          break

        case 'imageCaption':
          ref = captionRef
          break
      }

      const prev = store[field]

      if (ref) {
        let before = prev.slice(0, ref.selectionStart - (hotkey() ? 0 : 1))
        let after = prev.slice(ref.selectionStart)

        if (before.endsWith('{{')) {
          // Do nothing
        } else if (before.endsWith('{')) {
          before = before + '{'
        } else if (!before.endsWith('{')) {
          before = before + '{{'
        }

        if (!after.startsWith('}')) {
          after = '}}' + after
        }

        const next = `${before}${opt.label}${after}`
        ref.value = next
        setStore(field as any, next)
        ref.focus()
        ref.setSelectionRange(
          before.length + opt.label.length,
          before.length + opt.label.length,
          'none'
        )
      }

      setHotkey(false)
      setAuto('')
    }

  const importSchema = (schema?: ResponseSchema) => {
    setShowImport(false)
    if (!schema) {
      return
    }

    setStore(schema)
    close(true)
  }

  const close = (save: boolean) => {
    const saveTargetId = saveTarget()
    if (save) {
      preset.json

      const update = {
        history: store.history,
        response: store.response,
        imageCaption: store.imageCaption,
        schema: store.schema,
        systemPrompt: store.systemPrompt,
        jailbreak: store.jailbreak,
      }

      props.update(update)

      if (ctx.chat?._id && props.characterId && props.characterId.startsWith('temp-')) {
        const char = ctx.activeMap[props.characterId]
        if (!char) {
          toastStore.error(i18n.t.char_failedToLoad)
        } else {
          chatStore.upsertTempCharacter(ctx.chat._id, { ...char, json: update }, () =>
            toastStore.success(i18n.t.char_tempJsonSchemaUpdated)
          )
        }
      } else if (props.characterId) {
        characterStore.editPartialCharacter(props.characterId, { json: update })
      } else if (saveTargetId?.id) {
        presetStore.updatePreset(saveTargetId.id, { json: update })
      }
    }

    // if (save && typeof save !== 'boolean') {
    //   props.update(save)
    //   setStore('schema', save.schema)

    //   if (props.characterId) {
    //     characterStore.editPartialCharacter(props.characterId, { json: save })
    //   } else if (saveTargetId?.id) {
    //     presetStore.updatePreset(saveTargetId.id, { json: save })
    //   }
    // }

    setHistErr('')
    setResErr('')
    setShow(false)
  }

  const filename = createMemo(() =>
    props.characterId
      ? `schema-char-${props.characterId.slice(0, 4)}`
      : props.preset
      ? `schema-preset-${props.preset._id.slice(0, 4)}`
      : 'schema'
  )

  return (
    <div class="w-full justify-center">
      <FormLabel
        label={
          <div class="flex justify-between">
            <div>{i18n.t.char_jsonStructuredResponses}</div>
            <div class="flex gap-1">
              <Button size="pill" schema="secondary" onClick={() => setShowImport(true)}>
                {i18n.t.importCharacter}
              </Button>
              <Show when={store.schema.length}>
                <Button
                  size="pill"
                  schema="secondary"
                  onClick={() => downloadJson(store, filename())}
                >
                  {i18n.t.admin_add}
                </Button>
              </Show>
            </div>
          </div>
        }
        helperText={i18n.t.char_jsonStructuredResponsesHelper}
      />

      <div class="flex items-center gap-2">
        <Button onClick={() => setShow(true)}>{i18n.t.char_updateSchema}</Button>
        {props.children}
      </div>

      <Show when={show()}>
        <RootModal
          title={
            <>
              {i18n.t.char_editingSchema}
              <div class="text-600 !text-[1rem] font-normal">
                <Show when={props.characterId} fallback={saveTarget()?.label}>
                  {ctx.allBots[props.characterId!]?.name || i18n.t.characters}
                </Show>
              </div>
            </>
          }
          show={show()}
          maxWidth="half"
          close={() => setShow(false)}
          footer={
            <div class="flex w-full justify-between">
              <div>
                <Button onClick={runSchemaTest}>{i18n.t.char_testSchema}</Button>
              </div>
              <div class="flex gap-2">
                <Button schema="secondary" onClick={() => close(false)}>
                  {i18n.t.cancel}
                </Button>
                <Button onClick={() => close(true)}>{i18n.t.admin_send}</Button>
              </div>
            </div>
          }
        >
          <div class="flex flex-col gap-2 text-sm">
            <div class="flex w-full justify-center gap-2">
              <Pill type="premium">{i18n.t.char_jsonStructuredWarning}</Pill>

              <HelpModal
                title={i18n.t.admin_info}
                cta={
                  <Button size="sm">
                    <CircleHelp size={24} /> {i18n.t.guides}
                  </Button>
                }
                markdown={helpMarkdown}
              />
            </div>

            <Card class="relative">
              <Show when={auto() === 'response'}>
                <AutoComplete
                  options={vars()}
                  close={() => setAuto('')}
                  dir="down"
                  onSelect={onAutoComplete('response')}
                />
              </Show>
              <TextInput
                isMultiline
                fieldName="jsonSchemaResponse"
                label={<b>{i18n.t.char_responseTemplate}</b>}
                ref={(r) => (respRef = r)}
                onKeyDown={(ev) => {
                  if (ev.key === '{') setAuto('response')
                  if (ev.ctrlKey && ev.code === 'Space') {
                    setHotkey(true)
                    setAuto('response')
                  }
                }}
                helperText={
                  <>
                    <div>{i18n.t.char_responseTemplateHelper}</div>
                    <Show when={!!resErr()}>
                      <TitleCard type="rose">
                        {i18n.t.char_templateReferencesUndefined} {resErr()}
                      </TitleCard>
                    </Show>
                  </>
                }
                value={store.response}
                placeholder={i18n.t.char_responseTemplate}
                class="font-mono text-xs"
                onChange={(ev) => setStore('response', ev.currentTarget.value)}
              />
            </Card>

            <Card class="relative">
              <Show when={auto() === 'history'}>
                <AutoComplete
                  options={vars()}
                  close={() => setAuto('')}
                  dir="down"
                  onSelect={onAutoComplete('history')}
                />
              </Show>
              <TextInput
                class="font-mono text-xs"
                fieldName="jsonSchemaHistory"
                label={<b>{i18n.t.char_historyTemplate}</b>}
                ref={(r) => (histRef = r)}
                onKeyDown={(ev) => {
                  if (ev.key === '{') setAuto('history')
                  if (ev.ctrlKey && ev.code === 'Space') {
                    setHotkey(true)
                    setAuto('history')
                  }
                }}
                helperText={
                  <>
                    <>
                      <div>{i18n.t.char_historyTemplateHelper}</div>
                      <Show when={!!histErr()}>
                        <TitleCard type="rose">
                          {i18n.t.char_templateReferencesUndefined} {histErr()}
                        </TitleCard>
                      </Show>
                    </>
                  </>
                }
                isMultiline
                value={store.history}
                placeholder={i18n.t.char_historyTemplate}
                onChange={(ev) => setStore('history', ev.currentTarget.value)}
              />
            </Card>

            <Card class="relative">
              <Show when={auto() === 'imageCaption'}>
                <AutoComplete
                  options={vars()}
                  close={() => setAuto('')}
                  dir="down"
                  onSelect={onAutoComplete('imageCaption')}
                />
              </Show>
              <TextInput
                class="font-mono text-xs"
                fieldName="jsonSchemaImageCaption"
                label={<b>{i18n.t.char_imageCaptionTemplate}</b>}
                ref={(r) => (captionRef = r)}
                onKeyDown={(ev) => {
                  if (ev.key === '{') setAuto('imageCaption')
                  if (ev.ctrlKey && ev.code === 'Space') {
                    setHotkey(true)
                    setAuto('imageCaption')
                  }
                }}
                helperText={
                  <>
                    <>
                      <div>{i18n.t.char_imageCaptionTemplateHelper}</div>
                      <Show when={!!captionErr()}>
                        <TitleCard type="rose">
                          {i18n.t.char_templateReferencesUndefined} {captionErr()}
                        </TitleCard>
                      </Show>
                    </>
                  </>
                }
                isMultiline
                value={store.imageCaption}
                placeholder={i18n.t.char_imageCaptionTemplate}
                onChange={(ev) => setStore('imageCaption', ev.currentTarget.value)}
              />
            </Card>

            <Show
              when={
                !store.history.includes('{{response}}') || !store.response.includes('{{response}}')
              }
            >
              <SolidCard type="orange">
                {i18n.t.char_responseTemplateWarning}
              </SolidCard>
            </Show>

            <JsonSchema
              inherit={store.schema}
              update={(ev) => setStore('schema', ev)}
              onNameChange={onFieldNameChange}
            />

            <Show when={result()}>
              <code class="whitespace-pre-wrap">{JSON.stringify(result(), null, 2)}</code>
            </Show>
          </div>
        </RootModal>
      </Show>

      <ImportModal show={showImport()} close={importSchema} />
    </div>
  )
}

const ImportModal: Component<{ show: boolean; close: (schema?: ResponseSchema) => void }> = (
  props
) => {
  const i18n = i18nStore((s) => ({ t: s.t }))
  const onUpdate: ExtractProps<typeof FileInput>['onUpdate'] = async (files) => {
    const file = files[0]
    if (!file) return

    let curr: any
    try {
      const content = await getFileAsString(file)
      const json = JSON.parse(content)

      curr = json
      assertValid(
        {
          response: 'string',
          history: 'string',
          imageCaption: 'string?',
          fields: ['any?'],
          schema: ['any?'],
          separateCall: 'boolean?',
          systemPrompt: 'string?',
          jailbreak: 'string?',
        },
        json
      )

      const schema = curr.fields || curr.schema

      for (const field of schema) {
        curr = field
        assertValid({ type: { type: 'string' }, name: 'string' }, field)
      }

      props.close({
        response: json.response,
        history: json.history,
        imageCaption: json.imageCaption || '',
        schema,
        systemPrompt: json.systemPrompt || '',
        jailbreak: json.jailbreak || '',
      })
    } catch (ex: any) {
      toastStore.error(`${i18n.t.char_invalidJsonSchema} ${ex.message}`)
      console.error(ex)
      console.log('Failed at', JSON.stringify(curr))
    }
  }

  return (
    <RootModal
      show={props.show}
      close={props.close}
      footer={
        <>
          <Button schema="secondary" onClick={() => props.close()}>
            {i18n.t.close}
          </Button>
        </>
      }
    >
      <FileInput
        fieldName="import-schema"
        accept="text/json,application/json"
        label={i18n.t.char_jsonSchemaFile}
        onUpdate={onUpdate}
      />
    </RootModal>
  )
}
